/**
 * CanvasserMyPay — the My Pay page for a canvasser (Oct 26, 2026).
 *
 * Greg: "they will have a 'my pay' board as canvassers. can you build that to
 * show relevant information for the canvasser".
 *
 * One call, `canvasser_my_pay`, computed with the same rules that pay him
 * (canvasser_accrue_weekly_payouts, fn_canvasser_app_rate):
 *   • how he's paid (the Fee Schedule in his agreement, read from finance_settings)
 *   • today: on shift or not, hours, doors, contacts, appointments set
 *   • this week so far: an estimate (hours x rate + approved appointment fees),
 *     what's waiting on approval, the 1-appointment-per-2-hours minimum, every
 *     appointment with its fee, every session
 *   • every week already totaled, with where it stands (waiting on Chad's
 *     approval → approved, paying soon → paid)
 * Rendered by MyPay.tsx when the person on the page is a canvasser.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { useRealtimeTable } from "../../lib/useRealtimeTable";

interface Session {
  id: string;
  started_at: string;
  ended_at: string | null;
  hours: number;
  auto_closed: boolean;
  doors: number;
  contacts: number;
}
interface Appointment {
  id: string;
  submitted_at: string;
  street: string | null;
  city: string | null;
  status: "pending" | "approved" | "rejected" | string;
  carrier: string | null;
  confirmed: boolean;
  fee: number;
}
interface Week {
  id: string;
  period_start: string;
  period_end: string;
  hours: number;
  hours_recorded: number | null;
  hours_adjust_note: string | null;
  hourly_rate: number;
  hourly_amount: number;
  apps_confirmed: number;
  apps_unconfirmed: number;
  per_app_amount: number;
  adjustment: number;
  adjustment_note: string | null;
  total: number;
  status: "pending" | "approved" | "paid" | string;
  approved_at: string | null;
  paid_at: string | null;
}
interface MyPayData {
  member: { id: string; full_name: string; agreement_url: string | null; agreement_signed_at: string | null };
  rates: { hourly: number; app_confirmed: number; app_unconfirmed: number; unqualified_carriers: string[]; min_per_hour: number };
  open_session_started_at: string | null;
  today: { day: string; sessions: number; hours: number; doors: number; contacts: number; apps_set: number };
  week: {
    start: string;
    end: string;
    hours: number;
    hourly_est: number;
    sessions: Session[];
    apps: Appointment[];
    apps_set: number;
    apps_approved: number;
    apps_pending: number;
    apps_rejected: number;
    app_fees_earned: number;
    app_fees_waiting: number;
    est_total: number;
    apps_per_2h: number | null;
    needed: number;
    below_min: boolean;
  };
  weeks: Week[];
  totals: { paid_month: number; paid_year: number; awaiting_approval: number; approved_unpaid: number };
}

const n = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);
const hrs = (v: number) => (Math.round(v * 100) / 100).toFixed(2);
const plural = (k: number, one: string, many = one + "s") => `${k} ${k === 1 ? one : many}`;

// Dates and times read in Eastern, the team's clock. A date-only value is
// read at noon so it never slips a day.
function dateOnly(d: string, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }): string {
  return new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", ...opts });
}
function etTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
}
function etDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric" });
}
function addDays(d: string, k: number): string {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + k);
  return x.toISOString().slice(0, 10);
}
function monthName(): string {
  return new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", month: "long" });
}
function yearNow(): string {
  return new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", year: "numeric" });
}

function Card({ title, sub, right, children }: { title: string; sub?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-bardin-ink">{title}</div>
          {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Tile({ label, value, detail, tone = "ink" }: { label: string; value: string; detail?: string; tone?: "ink" | "green" | "teal" }) {
  const cls = tone === "green" ? "text-emerald-600" : tone === "teal" ? "text-bardin-teal" : "text-bardin-ink";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-xl font-extrabold ${cls}`}>{value}</div>
      {detail && <div className="text-[10px] text-slate-400">{detail}</div>}
    </div>
  );
}

function Num({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="text-lg font-bold text-bardin-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}

const CHIP = {
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  teal: "bg-sky-50 text-bardin-teal",
  slate: "bg-slate-100 text-slate-500",
} as const;
function Chip({ tone, children }: { tone: keyof typeof CHIP; children: ReactNode }) {
  return <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold ${CHIP[tone]}`}>{children}</span>;
}

function weekStatus(w: Week): ReactNode {
  if (w.status === "paid") return <Chip tone="green">Paid{w.paid_at ? ` ${etDay(w.paid_at).replace(/^\w+, /, "")}` : ""}</Chip>;
  if (w.status === "approved") return <Chip tone="teal">Approved, paying soon</Chip>;
  return <Chip tone="amber">Waiting on approval</Chip>;
}

/** Where the minimum stands: "2 set · 1.33 per 2 hrs" in green, or what's short in amber. */
function MinimumLine({ hours, set, needed, per2h, below, scope }: { hours: number; set: number; needed: number; per2h: number | null; below: boolean; scope: "today" | "this week" }) {
  if (hours < 1) {
    return <p className="text-xs text-slate-500">The minimum is 1 appointment for every 2 hours on shift. It's checked once you've worked an hour {scope}.</p>;
  }
  if (below) {
    return (
      <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
        {plural(set, "appointment")} set in {hrs(hours)} hrs {scope}. The minimum is 1 per 2 hours, so {needed} for {hrs(hours)} hrs.
      </p>
    );
  }
  return (
    <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
      {plural(set, "appointment")} set in {hrs(hours)} hrs {scope}{per2h != null ? ` (${per2h.toFixed(2)} per 2 hrs)` : ""}. You're at or above the minimum of 1 per 2 hours.
    </p>
  );
}

export default function CanvasserMyPay({ memberId, isSelf }: { memberId: string; isSelf: boolean }) {
  const [data, setData] = useState<MyPayData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openWeek, setOpenWeek] = useState<string | null>(null);
  const [idleMin, setIdleMin] = useState(60);

  // How long a session may sit idle before it ends (finance_settings, changeable without a deploy).
  useEffect(() => {
    supabase.from("finance_settings").select("value_jsonb").eq("key", "canvasser_session_idle_close_minutes").maybeSingle()
      .then(({ data: row }) => { const v = Number(row?.value_jsonb); if (v > 0) setIdleMin(v); });
  }, []);

  const load = useCallback(async () => {
    const { data: d, error } = await supabase.rpc("canvasser_my_pay", { p_member: memberId });
    if (error) { setErr(error.message); return; }
    setErr(null);
    setData(d as MyPayData);
  }, [memberId]);

  useEffect(() => { setData(null); load(); }, [load]);

  // Sessions, appointments and payouts change during the day; refresh after a short pause.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bump = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(load, 1500);
  }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useRealtimeTable("canvas_shifts", bump);
  useRealtimeTable("pending_canvas_appointments", bump);
  useRealtimeTable("canvasser_payouts", bump);

  if (err) return <div className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">Couldn't load your pay: {err}</div>;
  if (!data) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;

  const { rates, today, week, weeks, totals } = data;
  const unq = (rates.unqualified_carriers ?? []).join(" or ");
  const onTheWay = n(totals.awaiting_approval) + n(totals.approved_unpaid);
  const you = isSelf ? "you" : data.member.full_name.split(" ")[0];

  return (
    <div>
      {/* What was paid, and this week so far */}
      <div className="mb-4 mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile
          label="This week so far"
          value={fmtUSD(n(week.est_total))}
          detail={`${hrs(n(week.hours))} hrs · ${plural(week.apps_approved, "approved appt")} · estimate`}
          tone="teal"
        />
        <Tile
          label="On the way"
          value={fmtUSD(onTheWay)}
          detail={onTheWay ? `${fmtUSD(n(totals.approved_unpaid))} approved · ${fmtUSD(n(totals.awaiting_approval))} waiting` : "nothing waiting"}
        />
        <Tile label={`Paid in ${monthName()}`} value={fmtUSD(n(totals.paid_month))} tone="green" />
        <Tile label={`${yearNow()} so far`} value={fmtUSD(n(totals.paid_year))} />
      </div>

      {/* Today */}
      <Card
        title="Today"
        sub={dateOnly(today.day, { weekday: "long", month: "long", day: "numeric" })}
        right={
          data.open_session_started_at ? (
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> On shift since {etTime(data.open_session_started_at)}
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">Off shift</span>
          )
        }
      >
        <div className="grid grid-cols-4 gap-2 px-4 py-3">
          <Num label="Hours" value={hrs(n(today.hours))} />
          <Num label="Doors" value={String(today.doors)} />
          <Num label="Contacts" value={String(today.contacts)} />
          <Num label="Appts set" value={String(today.apps_set)} />
        </div>
        <div className="space-y-2 px-4 pb-3">
          <MinimumLine
            hours={n(today.hours)}
            set={today.apps_set}
            needed={Math.ceil(n(today.hours) * n(rates.min_per_hour))}
            per2h={n(today.hours) > 0 ? (today.apps_set / n(today.hours)) * 2 : null}
            below={n(today.hours) >= 1 && today.apps_set < n(today.hours) * n(rates.min_per_hour)}
            scope="today"
          />
          {!data.open_session_started_at && isSelf && (
            <p className="text-xs text-slate-500">
              Only time on shift is paid. When you start knocking, open the <Link to="/canvas" className="font-semibold text-bardin-teal underline">canvas map</Link> and tap On shift; tap it again when you're done.
            </p>
          )}
        </div>
      </Card>

      {/* This week */}
      <Card title="This week" sub={`${dateOnly(week.start)} – ${dateOnly(week.end)} · paid after the week closes`}>
        <div className="space-y-1.5 px-4 py-3 text-sm">
          <Line label={`Hourly: ${hrs(n(week.hours))} hrs × ${fmtUSD(n(rates.hourly))}`} value={fmtUSD(n(week.hourly_est))} />
          <Line label={`Approved appointments (${week.apps_approved})`} value={fmtUSD(n(week.app_fees_earned))} />
          {week.apps_pending > 0 && (
            <Line label={`Waiting on approval (${week.apps_pending}): added once approved`} value={fmtUSD(n(week.app_fees_waiting))} muted />
          )}
          <div className="flex items-center justify-between border-t border-slate-100 pt-1.5 font-semibold text-bardin-ink">
            <span>Estimate so far</span>
            <span>{fmtUSD(n(week.est_total))}</span>
          </div>
        </div>
        <div className="px-4 pb-3">
          <MinimumLine
            hours={n(week.hours)}
            set={week.apps_set}
            needed={week.needed}
            per2h={week.apps_per_2h != null ? n(week.apps_per_2h) : null}
            below={week.below_min}
            scope="this week"
          />
        </div>

        {week.apps.length > 0 && (
          <div className="border-t border-slate-100">
            <div className="px-4 pt-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Appointments</div>
            <div className="divide-y divide-slate-100">
              {week.apps.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-4 py-2 text-xs">
                  <div className="min-w-[10rem] flex-1">
                    <div className="font-medium text-bardin-ink">{a.street ?? "Address not recorded"}{a.city ? `, ${a.city}` : ""}</div>
                    <div className="text-slate-400">
                      {etDay(a.submitted_at)} · {a.carrier?.trim() || "insurance not given"}
                      {a.status !== "rejected" && !a.confirmed ? " · not confirmed or unqualified" : ""}
                    </div>
                  </div>
                  {a.status === "approved" ? <Chip tone="green">Approved</Chip> : a.status === "rejected" ? <Chip tone="slate">Not approved</Chip> : <Chip tone="amber">Waiting</Chip>}
                  <span className="w-12 text-right font-semibold text-bardin-ink">{fmtUSD(n(a.fee))}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {week.sessions.length > 0 && (
          <div className="border-t border-slate-100">
            <div className="px-4 pt-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Sessions</div>
            <div className="divide-y divide-slate-100">
              {week.sessions.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-4 py-2 text-xs">
                  <span className="font-medium text-bardin-ink">{etDay(s.started_at)}, {etTime(s.started_at)} – {s.ended_at ? etTime(s.ended_at) : "now"}</span>
                  <span>{hrs(n(s.hours))} hrs</span>
                  <span className="text-slate-400">{s.doors} doors · {s.contacts} contacts</span>
                  {s.auto_closed && <Chip tone="amber">ended automatically (idle)</Chip>}
                </div>
              ))}
            </div>
          </div>
        )}
        {week.sessions.length === 0 && week.apps.length === 0 && (
          <div className="border-t border-slate-100 px-4 py-4 text-center text-xs text-slate-400">No sessions or appointments yet this week.</div>
        )}
      </Card>

      {/* Weekly pay */}
      <Card title="Weekly pay" sub="Each week is totaled Monday morning, approved by Chad, then paid.">
        {weeks.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">No paid weeks yet. Your first week shows here the Monday after it ends.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {weeks.map((w) => {
              const apps = w.apps_confirmed + w.apps_unconfirmed;
              const adjusted = w.hours_recorded != null && n(w.hours_recorded) !== n(w.hours);
              const isOpen = openWeek === w.id;
              return (
                <div key={w.id}>
                  <button onClick={() => setOpenWeek(isOpen ? null : w.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left hover:bg-slate-50">
                    <div className="min-w-[10rem] flex-1">
                      <div className="text-sm font-medium text-bardin-ink">
                        Week of {dateOnly(w.period_start)} – {dateOnly(addDays(w.period_end, -1))}
                      </div>
                      <div className="text-[11px] text-slate-500">{hrs(n(w.hours))} hrs · {plural(apps, "appt")}</div>
                    </div>
                    {weekStatus(w)}
                    <div className="text-sm font-bold text-bardin-ink">{fmtUSD(n(w.total))}</div>
                  </button>
                  {isOpen && (
                    <div className="space-y-1 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
                      <Line label={`Hourly: ${hrs(n(w.hours))} hrs × ${fmtUSD(n(w.hourly_rate))}`} value={fmtUSD(n(w.hourly_amount))} />
                      <Line label={`Appointments: ${w.apps_confirmed} confirmed, ${w.apps_unconfirmed} not confirmed`} value={fmtUSD(n(w.per_app_amount))} />
                      {n(w.adjustment) !== 0 && <Line label={`Adjustment${w.adjustment_note ? `: ${w.adjustment_note}` : ""}`} value={fmtUSD(n(w.adjustment))} />}
                      {adjusted && (
                        <p className="text-sky-800">Hours changed from {hrs(n(w.hours_recorded))} to {hrs(n(w.hours))}{w.hours_adjust_note ? `: ${w.hours_adjust_note}` : ""}.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* How he's paid */}
      <Card title="How you're paid" sub="From the Fee Schedule in your agreement">
        <ul className="space-y-1 px-4 py-3 text-xs text-slate-600">
          <li>{fmtUSD(n(rates.hourly))} per hour on shift (from On shift to Off shift on the canvas map).</li>
          <li>{fmtUSD(n(rates.app_confirmed))} for each approved appointment where the homeowner names their insurance company.</li>
          <li>
            {fmtUSD(n(rates.app_unconfirmed))} when the insurance company isn't given{unq ? ` or is ${unq}` : ""}. Nothing for an appointment that isn't approved.
          </li>
          <li>A session ends on its own after {idleMin} minutes with no activity, so tap On shift again if you take a break and come back.</li>
          {data.member.agreement_url && (
            <li>
              <a href={data.member.agreement_url} target="_blank" rel="noreferrer" className="font-semibold text-bardin-teal underline">
                {isSelf ? "Your" : `${data.member.full_name.split(" ")[0]}'s`} signed agreement (PDF)
              </a>
            </li>
          )}
        </ul>
      </Card>

      <p className="text-[11px] text-slate-400">
        This week's number is an estimate until Chad approves the week. Questions about {you === "you" ? "your" : `${you}'s`} pay? Call (470) 823-8816.
      </p>
    </div>
  );
}

function Line({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={"flex items-center justify-between gap-3 " + (muted ? "text-slate-400" : "text-slate-600")}>
      <span>{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
